import { useRef, useState, useEffect } from "react"
import expandedIcon from "assets/svg/maximize-one.svg"
import addIcon from "assets/svg/plus.svg"
import { calculateOvertimeDuration } from "../utils"
import { ExpandDrawer } from "../components/ExpandDrawer"

interface OvertimeDetailsRowProps {
  key: number
  day: string
  date: string
  overtimeStart: string
  overtimeEnd: string
  reason: string
  status: string
  id: number | null
  onAddReason: (entry: {
    date: string
    overtimeStart: string
    overtimeEnd: string
    id: number | null
  }) => void
}

export const OvertimeDetailsRow = ({
  day,
  overtimeStart,
  date,
  overtimeEnd,
  reason,
  status,
  id,
  onAddReason,
}: OvertimeDetailsRowProps) => {
  const textRef = useRef<HTMLParagraphElement | null>(null)
  const [showExpandDrawer, setShowExpandDrawer] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const el = textRef.current
    if (el && reason) {
      setIsOverflowing(el.scrollHeight > el.clientHeight)
    }
  }, [reason])

  const overtimeDuration = calculateOvertimeDuration(overtimeStart, overtimeEnd)
  const isApproved = status?.toLowerCase() === "approved"
  const canAddReason = !reason && !isApproved

  return (
    <div className="grid grid-cols-[100px_1fr_1fr_1fr_1fr_100px] gap-3 items-center py-4 border-b border-b-slate-200">
      <p className="truncate min-w-0 hover:cursor-pointer">{day}</p>

      <p
        className="truncate min-w-0 text-sm font-semibold"
        style={{ margin: "0" }}
      >
        {overtimeStart}
      </p>

      <p>{overtimeEnd}</p>
      <p>{overtimeDuration}</p>

      <div>
        <p
          ref={textRef}
          className="min-w-0 line-clamp-3"
        >
          {reason || ""}
        </p>

        {canAddReason && (
          <span
            className="flex items-center gap-1 cursor-pointer"
            onClick={() =>
              onAddReason({
                date,
                overtimeStart,
                overtimeEnd,
                id,
              })
            }
          >
            <img
              src={addIcon}
              width={15}
              height={15}
            />
            add reason
          </span>
        )}

        {!reason && isApproved && (
          <span className="flex items-center gap-1 text-slate-400 cursor-not-allowed">
            <img
              src={addIcon}
              width={15}
              height={15}
              className="opacity-40"
            />
            add reason
          </span>
        )}

        {reason && isOverflowing && (
          <span
            onClick={() => setShowExpandDrawer(true)}
            className="flex text-sm items-center gap-2 bg-[#F1F5F9] px-3 py-1 rounded-full w-fit cursor-pointer"
          >
            <img
              src={expandedIcon}
              width={10}
              height={10}
            />
            Expand
          </span>
        )}

        <ExpandDrawer
          open={showExpandDrawer}
          onClose={() => setShowExpandDrawer(false)}
          title={`Timesheet for ${new Date(date).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })}`}
          rows={[
            { label: "Overtime start", value: overtimeStart },
            { label: "Overtime end", value: overtimeEnd },
            { label: "Ovr. duration", value: overtimeDuration },
            { label: "Reason", value: reason },
            { label: "Status", value: status },
          ]}
        />
      </div>

      <p
        className={
          isApproved
            ? "text-[#0F172B] capitalize text-xs"
            : status?.toLowerCase() === "rejected"
            ? "text-[#E7000B] capitalize text-xs"
            : "text-[#DA7705] capitalize text-xs"
        }
      >
        {status}
      </p>
    </div>
  )
}
